/** The exam item player — renders the four objective formats, collects
 * answers, submits for SERVER-side scoring, then shows the review.
 * Writing items get the AI writing coach; speaking items get the
 * continuous-voice AI partner. */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiError } from "../../api/http";
import * as api from "../../api/exams";
import { Button, Card, Spinner } from "../../components/ui";
import { TapToTranslate } from "../../components/TapToTranslate";
import { useT } from "../../i18n";
import { AudioPlayer } from "./AudioPlayer";
import { SpeakingPartner } from "./SpeakingPartner";
import { ApproachPanel } from "./ApproachPanel";
import { Stopwatch } from "./Stopwatch";
import { WritingCoach } from "./WritingCoach";
import { AskDoubt } from "../../components/AskDoubt";

type Answers = Record<string, unknown>;

interface FormatProps {
  payload: Record<string, unknown>;
  answers: Answers;
  setAnswer: (id: string, value: unknown) => void;
}

/** A German reading passage the learner can tap to reveal an English
 * rendering — the client's "translation on tap" applied to comprehension
 * texts. It's opt-in (tap to reveal), so the reading exercise stays intact
 * for anyone who doesn't need the crutch. */
function Passage({ text }: { text: string }) {
  return (
    <div className="rounded-control bg-canvas p-3 text-sm text-ink">
      <TapToTranslate text={text} />
    </div>
  );
}

function MatchingFormat({ payload, answers, setAnswer }: FormatProps) {
  const t = useT();
  const texts = (payload.texts as { id: string; text: string }[]) ?? [];
  const options = (payload.options as { key: string; text: string }[]) ?? [];
  return (
    <div className="space-y-4">
      <ol className="space-y-1 rounded-control bg-canvas p-3 text-sm">
        {options.map((option) => (
          <li key={option.key}>
            <strong>{option.key})</strong>{" "}
            <TapToTranslate text={option.text} />
          </li>
        ))}
      </ol>
      {texts.map((text) => (
        <div key={text.id} className="rounded-control border border-line p-3">
          <p className="text-sm text-ink">
            <strong>{text.id}.</strong> <TapToTranslate text={text.text} />
          </p>
          <select
            aria-label={`answer for text ${text.id}`}
            className="mt-2 rounded-control border border-line bg-surface px-2 py-1 text-sm"
            value={(answers[text.id] as string) ?? ""}
            onChange={(e) => setAnswer(text.id, e.target.value || undefined)}
          >
            <option value="">{t("practice.choose")}</option>
            {options.map((option) => (
              <option key={option.key} value={option.key}>
                {option.key}
              </option>
            ))}
          </select>
        </div>
      ))}
    </div>
  );
}

/** A recording (audio_url) OR a TTS transcript (audio_text) means this is a
 *  listening item. Both the player and the exam rubric key off it, so it
 *  lives in one place rather than being spelt out at each use. */
function hasAudio(payload: Record<string, unknown>): boolean {
  return Boolean(
    (typeof payload.audio_url === "string" && payload.audio_url) ||
    (typeof payload.audio_text === "string" && payload.audio_text),
  );
}


function MultipleChoiceFormat({ payload, answers, setAnswer }: FormatProps) {
  const questions = (payload.questions as { id: string; prompt: string; options: string[] }[]) ?? [];
  return (
    <div className="space-y-4">
      {hasAudio(payload) && (
        <AudioPlayer
          text={typeof payload.audio_text === "string" ? payload.audio_text : undefined}
          src={typeof payload.audio_url === "string" ? payload.audio_url : undefined}
        />
      )}
      {typeof payload.passage === "string" && payload.passage && (
        <Passage text={payload.passage} />
      )}
      {questions.map((q) => (
        <fieldset key={q.id} className="rounded-control border border-line p-3">
          <legend className="px-1 text-sm font-semibold text-ink">
            {q.id}. {q.prompt}
          </legend>
          {q.options.map((option, i) => (
            <label key={i} className="mt-1 flex items-center gap-2 text-sm text-ink">
              <input
                className="shrink-0"
                type="radio"
                name={`q-${q.id}`}
                checked={answers[q.id] === i}
                onChange={() => setAnswer(q.id, i)}
              />
              {option}
            </label>
          ))}
        </fieldset>
      ))}
    </div>
  );
}

function TrueFalseFormat({ payload, answers, setAnswer }: FormatProps) {
  const t = useT();
  const statements = (payload.statements as { id: string; statement: string }[]) ?? [];
  return (
    <div className="space-y-4">
      {hasAudio(payload) && (
        <AudioPlayer
          text={typeof payload.audio_text === "string" ? payload.audio_text : undefined}
          src={typeof payload.audio_url === "string" ? payload.audio_url : undefined}
        />
      )}
      {typeof payload.passage === "string" && payload.passage && (
        <Passage text={payload.passage} />
      )}
      {statements.map((s) => (
        <div key={s.id} className="flex flex-wrap items-center justify-between gap-3 rounded-control border border-line p-3">
          <p className="text-sm text-ink">
            <strong>{s.id}.</strong> <TapToTranslate text={s.statement} />
          </p>
          <div className="flex gap-2">
            {[true, false].map((value) => (
              <button
                key={String(value)}
                aria-label={`statement ${s.id} ${value}`}
                onClick={() => setAnswer(s.id, value)}
                className={`rounded-control border px-3 py-1 text-sm font-semibold ${answers[s.id] === value ? "border-brand bg-brand-soft text-brand-dark" : "border-line text-muted"}`}
              >
                {value ? t("practice.true") : t("practice.false")}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ClozeFormat({ payload, answers, setAnswer }: FormatProps) {
  const t = useT();
  const gaps = (payload.gaps as { id: string; options: string[] }[]) ?? [];
  return (
    <div className="space-y-4">
      <Passage text={String(payload.text ?? "")} />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {gaps.map((gap) => (
          <div key={gap.id} className="rounded-control border border-line p-3">
            <p className="text-sm font-semibold text-ink">[{gap.id}]</p>
            <select
              aria-label={`gap ${gap.id}`}
              className="mt-1 w-full rounded-control border border-line bg-surface px-2 py-1 text-sm"
              value={answers[gap.id] === undefined ? "" : String(answers[gap.id])}
              onChange={(e) =>
                setAnswer(gap.id, e.target.value === "" ? undefined : Number(e.target.value))
              }
            >
              <option value="">{t("practice.choose")}</option>
              {gap.options.map((option, i) => (
                <option key={i} value={i}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}

export const FORMATS: Record<string, (p: FormatProps) => JSX.Element> = {
  matching: MatchingFormat,
  multiple_choice: MultipleChoiceFormat,
  true_false: TrueFalseFormat,
  cloze: ClozeFormat,
};

/** How many answers an objective item expects — drives the "X unanswered"
 * counter. AI surfaces (writing/speaking) have no slots. */
export function answerSlots(itemType: string, payload: Record<string, unknown>): number {
  const len = (k: string) => (Array.isArray(payload[k]) ? (payload[k] as unknown[]).length : 0);
  if (itemType === "matching") return len("texts");
  if (itemType === "multiple_choice") return len("questions");
  if (itemType === "true_false") return len("statements");
  if (itemType === "cloze") return len("gaps");
  return 0;
}

export function ExamItemPlayerPage() {
  const t = useT();
  const { itemId = "" } = useParams();
  const [item, setItem] = useState<api.ExamItemDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Answers>({});
  const [result, setResult] = useState<api.SubmitResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [explanations, setExplanations] = useState<Record<string, string> | null>(null);
  const [explainConfigured, setExplainConfigured] = useState<boolean | null>(null);
  const [explainBusy, setExplainBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .examItem(itemId)
      .then((row) => {
        if (!cancelled) setItem(row);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : "error");
      });
    return () => {
      cancelled = true;
    };
  }, [itemId]);

  const setAnswer = (id: string, value: unknown) =>
    setAnswers((prev) => ({ ...prev, [id]: value }));

  const onSubmit = async () => {
    setBusy(true);
    try {
      setResult(await api.submitExamItem(itemId, answers));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "error");
    } finally {
      setBusy(false);
    }
  };

  const onRetry = () => {
    setAnswers({});
    setResult(null);
    setExplanations(null);
    setExplainConfigured(null);
  };

  const onExplain = async () => {
    setExplainBusy(true);
    try {
      const r = await api.explainItem(itemId, answers);
      setExplainConfigured(r.configured);
      setExplanations(Object.fromEntries(r.explanations.map((e) => [e.id, e.text])));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "error");
    } finally {
      setExplainBusy(false);
    }
  };

  if (error) {
    return (
      <Card>
        <p role="alert" className="text-sm text-danger">{error}</p>
      </Card>
    );
  }
  if (!item) {
    return (
      <div className="grid place-items-center py-12">
        <Spinner size={28} />
      </div>
    );
  }

  const Format = FORMATS[item.item_type];
  const backLink = `/practice/${item.board}/${item.level}/${item.part_code}`;
  const totalSlots = answerSlots(item.item_type, item.payload);
  const answered = Object.values(answers).filter((v) => v !== undefined).length;
  const unanswered = Math.max(0, totalSlots - answered);

  if (result) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <Card>
          <div className="text-center">
            <p className="text-4xl" aria-hidden>{result.score_percent >= 60 ? "🎉" : "💪"}</p>
            <h1 className="mt-2 text-2xl font-extrabold">{t("practice.result")}</h1>
            <p className="mt-2 text-lg">
              <strong data-testid="exam-score">{result.score_percent}%</strong>{" "}
              ({result.correct}/{result.total}) · {t("practice.best")}:{" "}
              {result.best_score_percent}%
            </p>
          </div>
          <ul className="mt-4 space-y-2">
            {result.review.map((row) => (
              <li
                key={row.id}
                className={`rounded-control border p-2 text-sm ${row.correct ? "border-success/40" : "border-danger/40"}`}
              >
                <strong>{row.id}.</strong> {row.correct ? "✓" : "✗"}{" "}
                {!row.correct && (
                  <>
                    {t("practice.yourAnswer")}: {String(row.given ?? "—")} ·{" "}
                    {t("practice.correctAnswer")}: <strong>{String(row.expected)}</strong>
                  </>
                )}
                {explanations?.[row.id] && (
                  <p className="mt-1 text-xs text-muted">💡 {explanations[row.id]}</p>
                )}
              </li>
            ))}
          </ul>
          {result.transcript && (
            <details className="mt-3 rounded-control border border-line bg-canvas p-3">
              <summary className="cursor-pointer text-sm font-semibold text-ink">
                {t("player.transcript")}
              </summary>
              <div className="mt-2 text-sm text-muted">
                <TapToTranslate block text={result.transcript} />
              </div>
            </details>
          )}
          <div className="mt-3 text-center">
            {explainConfigured === false ? (
              <p className="text-xs text-muted">{t("practice.explain.notConfigured")}</p>
            ) : explanations ? null : (
              <Button variant="secondary" onClick={onExplain} loading={explainBusy}>
                {t("practice.explain.get")}
              </Button>
            )}
          </div>
          <div className="mt-4 flex justify-center gap-3">
            <Button variant="secondary" onClick={onRetry}>
              {t("practice.tryAgain")}
            </Button>
            <Link to={backLink}>
              <Button>{t("practice.backToPart")}</Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div>
        <Link to={backLink} className="text-sm text-muted hover:text-brand">
          ← {item.part_title}
        </Link>
        <h1 className="mt-1 text-2xl font-extrabold">
          <TapToTranslate text={item.title} />
        </h1>
        <div className="text-sm text-muted">
          <TapToTranslate block text={String(item.payload.instruction ?? "")} />
        </div>
        {/* Audio in the payload means this is a listening part. The rubric
            ("you hear it twice") is a rule of the real exam, not a hint —
            without it a learner plays it once and thinks they failed. */}
        {hasAudio(item.payload) && (
          <div className="mt-3">
            <ApproachPanel kind="hoeren" />
          </div>
        )}
      </div>
      <Card>
        {Format ? (
          <>
            <div className="mb-4 flex items-center justify-between border-b border-line pb-3">
              <Stopwatch running={!result} />
              <span className={`text-sm font-semibold ${unanswered ? "text-warning" : "text-success"}`}>
                {unanswered
                  ? t("practice.unanswered", { n: unanswered })
                  : t("practice.allAnswered")}
              </span>
            </div>
            <Format payload={item.payload} answers={answers} setAnswer={setAnswer} />
            <div className="mt-5 text-right">
              <Button onClick={onSubmit} loading={busy}>
                {t("practice.submit")}
              </Button>
            </div>
          </>
        ) : item.item_type === "writing_prompt" ? (
          <WritingCoach itemId={item.id} payload={item.payload} />
        ) : (
          <SpeakingPartner itemId={item.id} payload={item.payload} level={item.level} />
        )}
      </Card>
      {/* Same reason as the lesson player: this is where a learner is stuck. */}
      <AskDoubt />
    </div>
  );
}
